import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import VendorApprovalSystem from '../components/VendorApprovalSystem'
import DirectMessages from '../components/DirectMessages'
import { secureMessageService } from '../services/secureMessageService'
import { messagingPatternDetection } from '../services/messagingPatternDetection'

interface VendorStats {
  totalContacts: number
  activeConversations: number
  pendingApprovals: number
  messagesSent: number
  messagesReceived: number
  responseRate: number
  averageResponseTime: number
}

export default function VendorLounge() {
  const { user } = useAuth()
  const [selectedConversationId, setSelectedConversationId] = useState<string>()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'messages' | 'approvals' | 'analytics'>('dashboard')
  const [vendorStats, setVendorStats] = useState<VendorStats>({
    totalContacts: 0,
    activeConversations: 0,
    pendingApprovals: 0,
    messagesSent: 0,
    messagesReceived: 0,
    responseRate: 0,
    averageResponseTime: 0
  })

  useEffect(() => {
    if (user?.role === 'verified_vendor') {
      loadVendorStats()
    }
  }, [user])

  const loadVendorStats = async () => {
    if (!user?.id) return

    try {
      // Get vendor conversations
      const conversations = secureMessageService.getUserConversations(user.id)
      
      // Get pending approvals
      const pendingContacts = JSON.parse(localStorage.getItem('pending_vendor_contacts') || '[]')
      const userPendingContacts = pendingContacts.filter((c: any) => 
        c.toVendorId === user.id && c.status === 'pending'
      )

      // Calculate stats (simplified for demo)
      const activeConversations = conversations.filter(conv => 
        conv.lastActivity > Date.now() - 7 * 24 * 60 * 60 * 1000 // Active in last week
      ).length

      setVendorStats({
        totalContacts: conversations.length,
        activeConversations,
        pendingApprovals: userPendingContacts.length,
        messagesSent: conversations.length * 5, // Mock data
        messagesReceived: conversations.length * 8, // Mock data
        responseRate: 85, // Mock data
        averageResponseTime: 2.4 // Mock data in hours
      })
    } catch (error) {
      console.error('Failed to load vendor stats:', error)
    }
  }

  if (!user || user.role !== 'verified_vendor') {
    return (
      <div className="vendor-access-denied">
        <div className="access-denied-content">
          <h2>🏪 Vendor Access Required</h2>
          <p>This area is exclusively for verified vendors.</p>
          <div className="access-info">
            <h3>How to become a verified vendor:</h3>
            <ol>
              <li>Complete vendor application</li>
              <li>Provide business documentation</li>
              <li>Pass verification review</li>
              <li>Maintain quality standards</li>
            </ol>
            <a href="/vendor-verification" className="apply-btn">
              Apply for Vendor Status
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="vendor-lounge">
      <div className="lounge-header">
        <div className="vendor-welcome">
          <h1>🏪 Vendor Lounge</h1>
          <p>Welcome back, <strong>{user.username}</strong></p>
          <div className="vendor-status">
            <span className="status-badge verified">✅ Verified Vendor</span>
            <span className="trust-score">Trust Score: 4.8/5</span>
          </div>
        </div>

        <div className="quick-stats">
          <div className="stat-item">
            <div className="stat-number">{vendorStats.activeConversations}</div>
            <div className="stat-label">Active Chats</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{vendorStats.pendingApprovals}</div>
            <div className="stat-label">Pending Requests</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{vendorStats.responseRate}%</div>
            <div className="stat-label">Response Rate</div>
          </div>
        </div>
      </div>

      <div className="lounge-navigation">
        <button 
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button 
          className={`nav-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          💬 Messages
          {vendorStats.activeConversations > 0 && (
            <span className="tab-badge">{vendorStats.activeConversations}</span>
          )}
        </button>
        <button 
          className={`nav-tab ${activeTab === 'approvals' ? 'active' : ''}`}
          onClick={() => setActiveTab('approvals')}
        >
          🛡️ Contact Requests
          {vendorStats.pendingApprovals > 0 && (
            <span className="tab-badge">{vendorStats.pendingApprovals}</span>
          )}
        </button>
        <button 
          className={`nav-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📈 Analytics
        </button>
      </div>

      <div className="lounge-content">
        {activeTab === 'dashboard' && (
          <div className="dashboard-content">
            <div className="dashboard-cards">
              <div className="dashboard-card">
                <div className="card-header">
                  <h3>📈 Business Metrics</h3>
                </div>
                <div className="metrics-grid">
                  <div className="metric">
                    <label>Total Contacts:</label>
                    <span>{vendorStats.totalContacts}</span>
                  </div>
                  <div className="metric">
                    <label>Messages Sent:</label>
                    <span>{vendorStats.messagesSent}</span>
                  </div>
                  <div className="metric">
                    <label>Messages Received:</label>
                    <span>{vendorStats.messagesReceived}</span>
                  </div>
                  <div className="metric">
                    <label>Avg Response Time:</label>
                    <span>{vendorStats.averageResponseTime}h</span>
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-header">
                  <h3>🛡️ Protection Status</h3>
                </div>
                <div className="protection-status">
                  <div className="status-item active">
                    <span className="status-icon">✅</span>
                    <span>Contact Request Filtering</span>
                  </div>
                  <div className="status-item active">
                    <span className="status-icon">✅</span>
                    <span>Spam Pattern Detection</span>
                  </div>
                  <div className="status-item active">
                    <span className="status-icon">✅</span>
                    <span>Rate Limiting Protection</span>
                  </div>
                  <div className="status-item active">
                    <span className="status-icon">✅</span>
                    <span>Message Encryption</span>
                  </div>
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-header">
                  <h3>💡 Vendor Tips</h3>
                </div>
                <div className="tips-list">
                  <div className="tip-item">
                    <span className="tip-icon">⚡</span>
                    <div className="tip-content">
                      <strong>Quick Responses:</strong> Fast replies improve your trust score
                    </div>
                  </div>
                  <div className="tip-item">
                    <span className="tip-icon">🔒</span>
                    <div className="tip-content">
                      <strong>Private Mode:</strong> Use for sensitive business discussions
                    </div>
                  </div>
                  <div className="tip-item">
                    <span className="tip-icon">🎯</span>
                    <div className="tip-content">
                      <strong>Quality Contacts:</strong> Approval system ensures serious buyers
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="recent-activity">
              <h3>📋 Recent Activity</h3>
              <div className="activity-list">
                <div className="activity-item">
                  <span className="activity-icon">👤</span>
                  <div className="activity-content">
                    <span>New contact request from <strong>john_doe</strong></span>
                    <span className="activity-time">2 hours ago</span>
                  </div>
                </div>
                <div className="activity-item">
                  <span className="activity-icon">💬</span>
                  <div className="activity-content">
                    <span>Message received from <strong>trusteduser</strong></span>
                    <span className="activity-time">4 hours ago</span>
                  </div>
                </div>
                <div className="activity-item">
                  <span className="activity-icon">✅</span>
                  <div className="activity-content">
                    <span>Approved contact request from <strong>mary_smith</strong></span>
                    <span className="activity-time">1 day ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="messages-content">
            <DirectMessages 
              selectedConversationId={selectedConversationId}
              onConversationSelect={setSelectedConversationId}
            />
          </div>
        )}

        {activeTab === 'approvals' && (
          <div className="approvals-content">
            <VendorApprovalSystem isVendor={true} userId={user.id} />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-content">
            <div className="analytics-header">
              <h2>📈 Messaging Analytics</h2>
              <p>Insights to help you improve customer relationships</p>
            </div>

            <div className="analytics-grid">
              <div className="analytics-card">
                <h3>📊 Response Performance</h3>
                <div className="performance-metrics">
                  <div className="performance-item">
                    <div className="performance-label">Response Rate</div>
                    <div className="performance-value good">{vendorStats.responseRate}%</div>
                    <div className="performance-trend">+5% from last month</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Avg Response Time</div>
                    <div className="performance-value">{vendorStats.averageResponseTime}h</div>
                    <div className="performance-trend">-0.3h from last month</div>
                  </div>
                  <div className="performance-item">
                    <div className="performance-label">Customer Satisfaction</div>
                    <div className="performance-value excellent">4.8/5</div>
                    <div className="performance-trend">+0.2 from last month</div>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <h3>🕒 Peak Activity Hours</h3>
                <div className="activity-chart">
                  <div className="chart-bars">
                    <div className="chart-bar" style={{ height: '60%' }}>
                      <span className="bar-label">9AM</span>
                    </div>
                    <div className="chart-bar" style={{ height: '80%' }}>
                      <span className="bar-label">12PM</span>
                    </div>
                    <div className="chart-bar" style={{ height: '90%' }}>
                      <span className="bar-label">3PM</span>
                    </div>
                    <div className="chart-bar" style={{ height: '70%' }}>
                      <span className="bar-label">6PM</span>
                    </div>
                    <div className="chart-bar" style={{ height: '40%' }}>
                      <span className="bar-label">9PM</span>
                    </div>
                  </div>
                  <p className="chart-insight">Peak messaging time: 2-4 PM</p>
                </div>
              </div>

              <div className="analytics-card">
                <h3>👥 Contact Sources</h3>
                <div className="source-breakdown">
                  <div className="source-item">
                    <span className="source-label">New Members</span>
                    <div className="source-bar">
                      <div className="source-fill" style={{ width: '60%' }}></div>
                    </div>
                    <span className="source-percent">60%</span>
                  </div>
                  <div className="source-item">
                    <span className="source-label">Trusted Members</span>
                    <div className="source-bar">
                      <div className="source-fill" style={{ width: '30%' }}></div>
                    </div>
                    <span className="source-percent">30%</span>
                  </div>
                  <div className="source-item">
                    <span className="source-label">Referrals</span>
                    <div className="source-bar">
                      <div className="source-fill" style={{ width: '10%' }}></div>
                    </div>
                    <span className="source-percent">10%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="improvement-suggestions">
              <h3>💡 Suggestions for Improvement</h3>
              <div className="suggestions-grid">
                <div className="suggestion-card">
                  <div className="suggestion-icon">⚡</div>
                  <div className="suggestion-content">
                    <h4>Reduce Response Time</h4>
                    <p>Try to respond within 2 hours during peak times for better customer satisfaction.</p>
                  </div>
                </div>
                <div className="suggestion-card">
                  <div className="suggestion-icon">📱</div>
                  <div className="suggestion-content">
                    <h4>Mobile Notifications</h4>
                    <p>Enable push notifications to catch messages faster during business hours.</p>
                  </div>
                </div>
                <div className="suggestion-card">
                  <div className="suggestion-icon">🤖</div>
                  <div className="suggestion-content">
                    <h4>Quick Responses</h4>
                    <p>Set up common response templates for frequently asked questions.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}